import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  Autocomplete,
} from '@mui/material';
import PropTypes from 'prop-types';

const PageFilters = ({ filters = [], onFilterChange, activeFilters = {} }) => {
  const handleFilterChange = (filterKey, value) => {
    onFilterChange(filterKey, value);
  };

  const renderFilter = filter => {
    const { key, type, label, options, multiple = false, ...props } = filter;
    const value = activeFilters[key] !== undefined ? activeFilters[key] : multiple ? [] : '';

    switch (type) {
      case 'select':
        return (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={value}
              onChange={e => handleFilterChange(key, e.target.value)}
              multiple={multiple}
              sx={{
                height: '32px',
                fontSize: '0.75rem',
                '& .MuiSelect-select': {
                  padding: '6px 8px',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'text.disabled',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'info.main',
                  borderWidth: '1px',
                },
              }}
              renderValue={
                multiple
                  ? selected => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(val => (
                          <Chip key={val} label={val} size="small" />
                        ))}
                      </Box>
                    )
                  : undefined
              }
              {...props}
            >
              {options.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'autocomplete':
        return (
          <Autocomplete
            size="small"
            sx={{ minWidth: 200 }}
            options={options}
            getOptionLabel={option => option.label || option}
            value={value}
            onChange={(event, newValue) => handleFilterChange(key, newValue)}
            multiple={multiple}
            renderInput={params => <TextField {...params} label={label} />}
            renderTags={
              multiple
                ? (value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.label || option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                : undefined
            }
            {...props}
          />
        );

      case 'date':
        return (
          <TextField
            size="small"
            type="date"
            label={label}
            value={value}
            onChange={e => handleFilterChange(key, e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ minWidth: 150 }}
            {...props}
          />
        );

      case 'dateRange':
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              type="date"
              label={`${label} From`}
              value={value.from || ''}
              onChange={e => handleFilterChange(key, { ...value, from: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              size="small"
              type="date"
              label={`${label} To`}
              value={value.to || ''}
              onChange={e => handleFilterChange(key, { ...value, to: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ minWidth: 150 }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      {filters.map(filter => (
        <Box key={filter.key}>{renderFilter(filter)}</Box>
      ))}
    </Box>
  );
};

PageFilters.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['select', 'autocomplete', 'date', 'dateRange']).isRequired,
      label: PropTypes.string.isRequired,
      options: PropTypes.array,
      multiple: PropTypes.bool,
    })
  ),
  onFilterChange: PropTypes.func.isRequired,
  activeFilters: PropTypes.object,
};

export default PageFilters;
